import type { SummaryListActionConfig } from "../services/types/ComponentTypes";

/** True when summary row is an unadjusted / draft return (is_draft = 1 or draft_status). */
export function isDraftSummaryRow(row: Record<string, any>): boolean {
  const draftVal = row?.is_draft ?? row?.txt_is_draft ?? row?.draft_status;
  if (draftVal === 1 || draftVal === "1" || draftVal === true) return true;
  const s = String(draftVal ?? "").trim().toLowerCase();
  return s === "draft" || s === "true";
}

/** True when summary row is voided. */
export function isVoidSummaryRow(row: Record<string, any>): boolean {
  const voidVal = row?.is_void ?? row?.txt_is_void;
  if (voidVal === 1 || voidVal === "1" || voidVal === true) return true;
  const status = String(row?.status ?? row?.txt_status ?? "")
    .trim()
    .toLowerCase();
  return status === "void";
}

/** Row-level visibility for summary action buttons (draft-only, status filters, etc.). */
export function isSummaryActionVisibleForRow(
  summaryAction: SummaryListActionConfig,
  row: Record<string, any>
): boolean {
  if ((summaryAction as { hiddenWhenVoid?: boolean }).hiddenWhenVoid === true) {
    if (isVoidSummaryRow(row)) return false;
  }

  if ((summaryAction as { visibleWhenNotDraft?: boolean }).visibleWhenNotDraft === true) {
    if (isDraftSummaryRow(row)) return false;
  }

  if (isVoidSummaryRow(row)) {
    const actionType = String(summaryAction.ActionType ?? "").toLowerCase();
    if (["edit", "delete", "approve", "submit", "void"].includes(actionType)) {
      if (actionType !== "void" || (summaryAction as { hiddenWhenVoid?: boolean }).hiddenWhenVoid) {
        if (actionType !== "void") return false;
      }
    }
  }

  if ((summaryAction as { visibleForDraftOnly?: boolean }).visibleForDraftOnly === true) {
    if (!isDraftSummaryRow(row)) return false;
  }

  const visibleForStatus = summaryAction.visibleForStatus;
  if (Array.isArray(visibleForStatus) && visibleForStatus.length > 0) {
    const rowStatusRaw =
      row.status ?? row.current_state ?? row.currentStatus ?? "";
    const rowStatus = String(rowStatusRaw).trim().toLowerCase();
    const allowed = visibleForStatus
      .map((s) => String(s).trim().toLowerCase())
      .includes(rowStatus);
    if (!allowed) return false;
  }

  const visibleWhenField = (summaryAction as { visibleWhenField?: string })
    .visibleWhenField;
  if (visibleWhenField) {
    const fieldValue = String(row[visibleWhenField] ?? "").trim();
    if (
      (summaryAction as { visibleWhenFieldNotEmpty?: boolean })
        .visibleWhenFieldNotEmpty === true &&
      !fieldValue
    ) {
      return false;
    }
    if (
      (summaryAction as { visibleWhenFieldEmpty?: boolean }).visibleWhenFieldEmpty ===
        true &&
      fieldValue
    ) {
      return false;
    }
  }

  return true;
}
